import React from "react";
import {
  Controller,
  type Control,
  type FieldErrors,
  type FieldValues,
} from "react-hook-form";
import { REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";
import GenericField, { fieldTypeOptions } from "../../GenericField";
import {
  ticketingIconClassNames,
  getTicketingIcon,
  getTicketStatusName,
} from "../../../TicketingUtils";
import "./MappingFieldRow.scss";

interface MappingFieldOption {
  id: string;
}

interface MappingFieldDefinition {
  input?: string;
  required?: boolean;
  icon?: string;
  label?: string;
  name?: string;
  options?: MappingFieldOption[];
}

interface MappingFieldRowData {
  field_from?: MappingFieldDefinition;
  field_to?: MappingFieldDefinition;
}

type NestedErrors = {
  [key: string]: NestedErrors | undefined;
};

export interface MappingFieldRowProps {
  row?: MappingFieldRowData;
  value?: Record<string, unknown>;
  errors?: FieldErrors<FieldValues>;
  control?: Control<FieldValues>;
  registerPrefix?: string;
  mappedFields?: string[];
  onRowChange: (value: Record<string, unknown>) => void;
  placeholder?: string;
}

const MappingFieldRow: React.FC<MappingFieldRowProps> = (props) => {
  const {
    row,
    value: mappingValue,
    errors,
    control,
    registerPrefix,
    mappedFields,
    onRowChange,
  } = props;

  const getMappingField = (
    field: MappingFieldDefinition | undefined,
    labelFieldLabel: string | undefined,
    labelFieldName: string | undefined
  ) => {
    if (field?.input === fieldTypeOptions.LABEL) {
      const statusName = getTicketStatusName(field?.icon);

      return (
        <div
          className={`mapping-field-cell-label input-label ${
            field?.required ? "is-required" : "is-optional"
          } ${
            ticketingIconClassNames?.[field?.icon ?? ""]
              ? ticketingIconClassNames?.[field?.icon ?? ""]
              : ""
          }`}
        >
          {field?.icon &&
            (statusName ? (
              <div className={`cell-status-dot ${statusName}`} />
            ) : (
              <div className={"cell-icon-wrap"}>
                <img
                  className={"cell-icon"}
                  alt={field?.label}
                  src={getTicketingIcon(field?.icon, "S")}
                />
              </div>
            ))}
          <div className={"mapping-field-cell-label-text"}>{field?.label}</div>
          {field?.required === false && (
            <div className={"label-helper label-helper-in-field"}>Optional</div>
          )}
        </div>
      );
    } else {
      // The error object is indexed dynamically through the register prefix path
      // (e.g. "a.b.fieldName"). RHF's FieldErrors map doesn't expose this nested
      // string-path access in its type, so we read it through a structural index type.
      const nestedErrors = errors as NestedErrors | undefined;

      return (
        <div className={"mapping-field-cell-input"}>
          <Controller
            name={`${registerPrefix}.${labelFieldName}`}
            render={({ field: { onChange, onBlur, value: rowValue } }) => {
              let currentOptions = field?.options?.filter(
                (el) =>
                  !mappedFields?.includes(el.id) ||
                  (!!rowValue && rowValue?.includes(el.id))
              );

              return (
                <GenericField
                  {...props}
                  {...field}
                  onChange={(rowVal: unknown) => {
                    // Use `value || null` to ensure that the input's value is never `undefined`.
                    // React Hook Form doesn't update the input's value when it's set to `undefined`,
                    // so using `value || null` guarantees that the value will always be defined and can be properly updated in the form.
                    onChange(rowVal || null);
                    onRowChange({
                      ...mappingValue,
                      [labelFieldName ?? ""]: rowVal || null,
                    });
                  }}
                  onBlur={onBlur}
                  value={rowValue}
                  label={""}
                  options={currentOptions}
                  control={control}
                  size={"max"}
                  placeholder={
                    props?.placeholder ||
                    `Select Mapping for ${labelFieldLabel}`
                  }
                  error={
                    nestedErrors?.[registerPrefix?.split(".")?.[0] ?? ""]?.[
                      registerPrefix?.split(".")?.[1] ?? ""
                    ]?.[labelFieldName ?? ""]
                  }
                />
              );
            }}
            defaultValue={mappingValue?.[labelFieldName ?? ""]}
            control={control}
            rules={{
              ...(field?.required && { required: REQUIRED_ERROR_MESSAGE }),
            }}
          />
        </div>
      );
    }
  };

  return (
    <div className={"mapping-field-row mapping-data"}>
      <div className={"mapping-cell"}>
        {getMappingField(
          row?.field_from,
          row?.field_to?.label,
          row?.field_to?.name
        )}
      </div>
      <span className="mapping-cell-arrow">
        <i className="seem-icon seem-icon-next-arrow" />
      </span>
      <div className={"mapping-cell"}>
        {getMappingField(
          row?.field_to,
          row?.field_from?.label,
          row?.field_from?.name
        )}
      </div>
    </div>
  );
};

export default MappingFieldRow;
